import { Button, type Column } from "@legal-500/legal500-design-system";
import { useCallback, useMemo, useState } from "react";
import { DataTable } from "../../../components/DataTable/DataTable.js";
import {
  pipelineService,
  type MissingNewAxisIdsItem,
  type MissingNewAxisIdsScan,
} from "../../../api/pipeline.service.js";
import { formatDateTime } from "../../../utils/index.js";
import { useMissingNewAxisIdsFix } from "./useMissingNewAxisIdsFix.js";
import styles from "./PipelineBrowser.module.css";

function nameWithId(name: string | null, id: number | null): string {
  if (id === null) return name ?? "-";
  return name ? `${name} (${id})` : String(id);
}

function describeReason(item: MissingNewAxisIdsItem): string {
  if (item.repairable) return "Repairable";
  if (item.unresolved.length === 0) return "Outside the bridge";
  const fields = item.unresolved.map(({ field, reason }) => `${field} ${reason}`);
  return `Outside the bridge: ${fields.join(", ")}`;
}

function MissingResearchIdsSection() {
  const [scan, setScan] = useState<MissingNewAxisIdsScan | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const runScan = useCallback(async () => {
    setScanning(true);
    setScanError(null);
    try {
      setScan(await pipelineService.getMissingNewAxisIdsStats());
    } catch (err) {
      setScan(null);
      setScanError(err instanceof Error ? err.message : "Scan failed");
    } finally {
      setScanning(false);
    }
  }, []);

  const { running, progress, error: applyError, start, stop } = useMissingNewAxisIdsFix(runScan);

  const columns: Column<MissingNewAxisIdsItem>[] = useMemo(
    () => [
      {
        header: "External id",
        accessor: "externalId",
        render: (_value: unknown, row: MissingNewAxisIdsItem) => row.externalId ?? "-",
      },
      {
        header: "Publication",
        accessor: "publicationName",
        render: (_value: unknown, row: MissingNewAxisIdsItem) =>
          nameWithId(row.publicationName, row.publicationId),
      },
      {
        header: "Practice area",
        accessor: "practiceAreaName",
        render: (_value: unknown, row: MissingNewAxisIdsItem) =>
          nameWithId(row.practiceAreaName, row.practiceAreaId),
      },
      {
        header: "Status",
        accessor: "status",
        render: (_value: unknown, row: MissingNewAxisIdsItem) => row.status ?? "-",
      },
      {
        header: "Created",
        accessor: "createdAt",
        render: (_value: unknown, row: MissingNewAxisIdsItem) =>
          row.createdAt ? formatDateTime(row.createdAt) : "-",
      },
      {
        header: "Missing",
        accessor: "missing",
        render: (_value: unknown, row: MissingNewAxisIdsItem) => row.missing.join(", "),
      },
      {
        header: "Reason",
        accessor: "unresolved",
        render: (_value: unknown, row: MissingNewAxisIdsItem) => describeReason(row),
      },
    ],
    [],
  );

  const canApply = scan !== null && scan.repairable > 0 && !scanning && !running;

  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Missing research ids</div>
      <div className={styles.infoBanner}>
        Apply fills only the empty ids of repairable items (enriched while research-api was failing,
        resolvable now) and skips items outside the bridge, whose publication, practice area or
        table <code>research_wp_mapping</code> does not know yet.
      </div>

      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={scanning || running}
          onClick={() => void runScan()}
        >
          {scanning ? "Scanning…" : "Scan"}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!canApply}
          onClick={() => void start()}
        >
          {running ? "Applying…" : "Apply"}
        </Button>
        <Button variant="outlined" color="error" size="small" disabled={!running} onClick={stop}>
          Stop
        </Button>
      </div>

      {scan && (
        <div className={styles.syncStateRow}>
          <span>
            Total: <strong>{scan.total}</strong> · Repairable: <strong>{scan.repairable}</strong> ·
            Outside the bridge: <strong>{scan.outsideBridge}</strong>
          </span>
          {scan.listed < scan.total && (
            <span className={styles.mappingHint}>
              Showing the newest {scan.listed} of {scan.total}
            </span>
          )}
        </div>
      )}

      {progress.batch > 0 && (
        <div className={styles.syncStateMessage} role="status">
          Batch {progress.batch}: {progress.updated} families, {progress.processingItems} items,{" "}
          {progress.testimonials} testimonials written, {progress.remaining ?? "-"} remaining
        </div>
      )}

      {scanError && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {scanError}
        </div>
      )}
      {applyError && (
        <div className={styles.syncStateMessage} role="alert">
          Error: {applyError}
        </div>
      )}

      {scan && (
        <div className={styles.tableContainer}>
          <DataTable
            data={scan.items}
            columns={columns}
            rowKey="processingItemId"
            size="small"
            emptyMessage="No items with missing research ids."
          />
        </div>
      )}
    </div>
  );
}

export default MissingResearchIdsSection;
